import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiGet, apiPost, ApiError } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

/** One row of GET /api/surveys. */
interface SurveyRow {
  survey_id: string
  title: string
  created_at: string
  updated_at: string
  latest_published: number | null
  has_draft: boolean
}

/**
 * A campaign's surveys. Authoring is Campaign-Manager-and-above (canBuildUniverses,
 * the same line as universes); the list is readable by anyone with campaign
 * access, because a capture client renders from the published definition.
 */
export function SurveyList() {
  const { campaignId, campaign } = useSelectedCampaign()
  const navigate = useNavigate()

  const { data, isPending } = useQuery({
    queryKey: ['surveys', campaignId],
    queryFn: () => apiGet<{ surveys: SurveyRow[] }>(`/surveys?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })

  async function createSurvey() {
    const title = prompt('Survey title')?.trim()
    if (!title || !campaignId) return
    try {
      const { survey_version_id } = await apiPost<{ survey_version_id: string }>('/surveys', {
        campaign_id: campaignId,
        title,
      })
      void navigate({ to: '/surveys/$versionId', params: { versionId: survey_version_id } })
    } catch (error) {
      alert(error instanceof ApiError ? error.message : 'Could not create the survey')
    }
  }

  const surveys = data?.surveys ?? []

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Surveys</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Questions asked at the door and on the phone. Answers are filterable in a universe.
          </p>
        </div>
      </div>

      {campaign?.can_build_universes ? (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => void createSurvey()}
            className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
          >
            New survey
          </button>
        </div>
      ) : (
        <p className="mt-5 text-sm text-ink-muted">Authoring a survey needs a Campaign Manager role.</p>
      )}

      <div className="mt-5 overflow-x-auto">
        {isPending ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : surveys.length === 0 ? (
          <p className="text-sm text-ink-muted">No surveys yet.</p>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left text-ink-muted">
                <th className="py-2 pr-4 font-medium">Title</th>
                <th className="py-2 pr-4 font-medium">Published</th>
                <th className="py-2 pr-4 font-medium">Draft</th>
                <th className="py-2 pr-4 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.survey_id} className="border-b border-line/60">
                  <td className="py-2 pr-4 font-medium text-ink">{s.title}</td>
                  <td className="py-2 pr-4 text-ink-muted">
                    {s.latest_published ? `v${s.latest_published}` : '—'}
                  </td>
                  <td className="py-2 pr-4 text-ink-muted">{s.has_draft ? 'open' : '—'}</td>
                  <td className="py-2 pr-4 text-ink-muted">
                    {new Date(s.updated_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
